import { useMinimumPercentageOffToDisplay } from '@shared/hooks/useMinimumPercentageOffToDisplay';
import { getPercentageOff, translatedPrice } from '@shared/utils/accommodationPrice';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import { useMemo } from 'react';
import type { HotelRoomBlockPricingValues, RawJoyHotelPricingInfo, RoomBlockWithPricePerNight, StandardHotelPricingValues } from '@shared/types/Accommodations.types';

export const useStandardHotelRoomPriceMapping = () => {
  const navigatorLanguage = readNavigatorLanguage();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();
  return useMemo(() => {
    return (pricingInfo: RawJoyHotelPricingInfo): StandardHotelPricingValues | null => {
      const { perNightPerOccupancyBaseAmount, perNightPerOccupancyStrikeoutBaseAmount, totalRate: totalRateAmount, allInTotalRate, onsiteChargesTotalRate } = pricingInfo;

      // Handle null pricing
      if (!perNightPerOccupancyBaseAmount) {
        return null;
      }

      const shouldShowDiscount = (perNightPerOccupancyStrikeoutBaseAmount?.valueInMinorUnits ?? 0) > perNightPerOccupancyBaseAmount.valueInMinorUnits;
      const digits = perNightPerOccupancyBaseAmount.digits ?? 2;
      const currency = perNightPerOccupancyStrikeoutBaseAmount?.currency ?? perNightPerOccupancyBaseAmount.currency;

      const divisor = Math.pow(10, digits);

      // We ceil the prices to whole number for discount display purposes
      const ceiledBaseMajor = Math.ceil(perNightPerOccupancyBaseAmount.valueInMinorUnits / divisor);
      const ceiledStrikeoutMajor =
        shouldShowDiscount && perNightPerOccupancyStrikeoutBaseAmount
          ? Math.ceil(perNightPerOccupancyStrikeoutBaseAmount.valueInMinorUnits / Math.pow(10, perNightPerOccupancyStrikeoutBaseAmount.digits ?? digits))
          : 0;

      // Discount is the difference between ceiled values, converted back to minor units
      const discountMinorUnits = (ceiledStrikeoutMajor - ceiledBaseMajor) * divisor;
      const discountAmount = discountMinorUnits / divisor;

      return {
        discountPercentage: shouldShowDiscount
          ? getPercentageOff(
              minimumPercentageOffToDisplayValue,
              perNightPerOccupancyBaseAmount.valueInMinorUnits,
              perNightPerOccupancyStrikeoutBaseAmount?.valueInMinorUnits,
              digits,
              perNightPerOccupancyStrikeoutBaseAmount?.digits,
            )
          : undefined,
        nightlyBaseRateString: translatedPrice(perNightPerOccupancyBaseAmount.valueInMinorUnits, digits, perNightPerOccupancyBaseAmount.currency, navigatorLanguage),
        strikeoutNightlyBaseRateString:
          shouldShowDiscount && perNightPerOccupancyStrikeoutBaseAmount
            ? translatedPrice(
                perNightPerOccupancyStrikeoutBaseAmount.valueInMinorUnits,
                perNightPerOccupancyStrikeoutBaseAmount.digits ?? digits,
                perNightPerOccupancyStrikeoutBaseAmount.currency,
                navigatorLanguage,
              )
            : undefined,
        discountAmount,
        discountAmountString: discountMinorUnits > 0 ? translatedPrice(discountMinorUnits, digits, currency, navigatorLanguage) : undefined,
        // strikeoutTotalRateString: pricingInfo.strikeoutTotalRate ? formatCurrencyValue(pricingInfo.strikeoutTotalRate) : undefined,
        totalRateString: translatedPrice(totalRateAmount.valueInMinorUnits, totalRateAmount.digits ?? 2, totalRateAmount.currency, navigatorLanguage),
        nightlyBaseRate: Math.round(perNightPerOccupancyBaseAmount.valueInMinorUnits / Math.pow(10, digits)),
        // Exact (unrounded) major-unit total: cash-fund subtracts the fund balance from this, so dropping
        // cents here would over-cover (a $400.49 stay vs a $400 fund must NOT read as $0 out-of-pocket).
        totalRate: totalRateAmount.valueInMinorUnits / Math.pow(10, totalRateAmount.digits ?? 2),
        totalRateCurrency: totalRateAmount.currency,
        totalRateDigits: totalRateAmount.digits ?? 2,
        // Mapped in both arms — the experiment is decided at the display/sort layer, so the mapper
        // stays flag-free and `totalRate` above keeps meaning "what Joy collects" for the cash fund.
        allInTotalRateString: allInTotalRate
          ? translatedPrice(allInTotalRate.valueInMinorUnits, allInTotalRate.digits ?? 2, allInTotalRate.currency, navigatorLanguage)
          : undefined,
        allInTotalRate: allInTotalRate ? allInTotalRate.valueInMinorUnits / Math.pow(10, allInTotalRate.digits ?? 2) : undefined,
        // Left undefined (not 0) when the backend could not determine a charge, so copy never
        // promises property charges are included in a figure that does not contain them.
        onsiteChargesTotalRate: onsiteChargesTotalRate ? onsiteChargesTotalRate.valueInMinorUnits / Math.pow(10, onsiteChargesTotalRate.digits ?? 2) : undefined,
      };
    };
  }, [minimumPercentageOffToDisplayValue, navigatorLanguage]);
};

export type UseHotelRoomBlockPriceMappingReturn = ReturnType<typeof useHotelRoomBlockPriceMapping>;

export const useHotelRoomBlockPriceMapping = (eventCurrencyCode: string | null | undefined) => {
  const navigatorLanguage = readNavigatorLanguage();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();
  return useMemo(() => {
    return (roomBlock: RoomBlockWithPricePerNight): HotelRoomBlockPricingValues => {
      const { pricePerNight, strikeoutPricePerNight } = roomBlock;
      const shouldShowDiscount = (strikeoutPricePerNight ?? 0) > pricePerNight;
      const currency = eventCurrencyCode || 'USD';
      const pricePerNightMinorUnits = Math.round(pricePerNight * 100);
      const strikeoutMinorUnits = typeof strikeoutPricePerNight === 'number' ? Math.round(strikeoutPricePerNight * 100) : undefined;

      const ceiledBaseMajor = Math.ceil(pricePerNight);
      const ceiledStrikeoutMajor = shouldShowDiscount && typeof strikeoutPricePerNight === 'number' ? Math.ceil(strikeoutPricePerNight) : 0;
      const discountMinorUnits = (ceiledStrikeoutMajor - ceiledBaseMajor) * 100;
      const discountAmount = discountMinorUnits / 100;

      return {
        discountPercentage: shouldShowDiscount ? getPercentageOff(minimumPercentageOffToDisplayValue, pricePerNight * 100, (strikeoutPricePerNight ?? 0) * 100, 2, 2) : undefined,
        nightlyBaseRateString: translatedPrice(pricePerNightMinorUnits, 2, currency, navigatorLanguage),
        strikeoutNightlyBaseRateString:
          typeof strikeoutMinorUnits === 'number' && strikeoutMinorUnits > 0 ? translatedPrice(strikeoutMinorUnits, 2, currency, navigatorLanguage) : undefined,
        discountAmount,
        discountAmountString: discountMinorUnits > 0 ? translatedPrice(discountMinorUnits, 2, currency, navigatorLanguage) : undefined,
        nightlyBaseRate: pricePerNight,
      };
    };
  }, [minimumPercentageOffToDisplayValue, navigatorLanguage, eventCurrencyCode]);
};
